import { forwardRef } from "react";

const CVPreview = forwardRef(({ cvData }, ref) => {
  // Color scheme presets
  const colorSchemes = {
    ocean: { primary: "#0077B6", accent: "#CAF0F8" },
    forest: { primary: "#2D6A4F", accent: "#D8F3DC" },
    burgundy: { primary: "#800020", accent: "#FFE4E6" },
    slate: { primary: "#475569", accent: "#F1F5F9" },
    royal: { primary: "#7B2D8E", accent: "#F3E8FF" },
    copper: { primary: "#B87333", accent: "#FEF3E2" },
    midnight: { primary: "#1A1A2E", accent: "#E2E8F0" },
  };

  // Template-based styling
  const getTemplateStyles = () => {
    const template = cvData.template || "professional";
    const colorScheme = cvData.colorScheme || "default";
    
    // Get base template styles
    let baseStyles;
    switch(template) {
      case "modern":
        baseStyles = {
          primary: "#059669",
          accent: "#D1FAE5",
          headerBg: "transparent",
          sectionBorder: true,
        };
        break;
      case "creative":
        baseStyles = {
          primary: "#7C3AED",
          accent: "#EDE9FE",
          headerBg: "transparent",
          sectionBorder: true,
        };
        break;
      case "executive":
        baseStyles = {
          primary: "#1E3A5F",
          accent: "#D4AF37",
          secondary: "#F8F6F0",
          headerBg: "#1E3A5F",
          headerText: "#FFFFFF",
          sectionBorder: false,
        };
        break;
      case "minimalist":
        baseStyles = {
          primary: "#374151",
          accent: "#F9FAFB",
          headerBg: "transparent",
          sectionBorder: false,
          minimal: true,
        };
        break;
      case "academic":
        baseStyles = {
          primary: "#7C2D12",
          accent: "#FEF3C7",
          secondary: "#FFFBEB",
          headerBg: "transparent",
          sectionBorder: true,
          academic: true,
        };
        break;
      case "technical":
        baseStyles = {
          primary: "#0EA5E9",
          accent: "#0F172A",
          secondary: "#F0F9FF",
          headerBg: "#0F172A",
          headerText: "#E2E8F0",
          sectionBorder: false,
          technical: true,
        };
        break;
      default: // professional
        baseStyles = {
          primary: "#18181B",
          accent: "#F4F4F5",
          headerBg: "transparent",
          sectionBorder: true,
        };
    }
    
    // Apply color scheme overrides
    if (colorScheme !== "default") {
      if (colorScheme === "custom") {
        // Use custom colors
        const customPrimary = cvData.customPrimaryColor || baseStyles.primary;
        const customAccent = cvData.customAccentColor || baseStyles.accent;
        baseStyles.primary = customPrimary;
        baseStyles.accent = customAccent;
        // For templates with header backgrounds, update those too
        if (baseStyles.headerBg && baseStyles.headerBg !== "transparent") {
          baseStyles.headerBg = customPrimary;
        }
      } else if (colorSchemes[colorScheme]) {
        // Use preset color scheme
        const preset = colorSchemes[colorScheme];
        baseStyles.primary = preset.primary;
        baseStyles.accent = preset.accent;
        // For templates with header backgrounds, update those too
        if (baseStyles.headerBg && baseStyles.headerBg !== "transparent") {
          baseStyles.headerBg = preset.primary;
        }
      }
    }
    
    return baseStyles;
  };

  // Font mapping
  const getFontFamily = () => {
    const font = cvData.font || "helvetica";
    switch(font) {
      case "georgia":
        return "Georgia, 'Times New Roman', serif";
      case "arial":
        return "Arial, sans-serif";
      case "times":
        return "'Times New Roman', Times, serif";
      case "palatino":
        return "'Palatino Linotype', 'Book Antiqua', Palatino, serif";
      default: // helvetica
        return "Helvetica, Arial, sans-serif";
    }
  };

  const styles = getTemplateStyles();
  const fontFamily = getFontFamily();

  // Format description as bullet points
  const formatDescription = (description) => {
    if (!description) return null;
    
    const lines = description.split('\n').filter(line => line.trim());
    
    return (
      <ul style={{ 
        margin: "6px 0 0 0", 
        padding: "0 0 0 20px", 
        listStyleType: "disc" 
      }}>
        {lines.map((line, idx) => {
          const text = line.replace(/^[•\-\*]\s*/, '').trim();
          return text ? (
            <li key={idx} style={{ 
              fontSize: "11px", 
              lineHeight: "1.5", 
              marginBottom: "3px", 
              color: "#0B1220" 
            }}>
              {text}
            </li>
          ) : null;
        })}
      </ul>
    );
  };

  return (
    <div
      ref={ref}
      style={{
        width: "210mm",
        minHeight: "297mm",
        backgroundColor: "white",
        padding: "20mm",
        fontFamily: fontFamily,
        fontSize: "11px",
        lineHeight: "1.4",
        color: "#0B1220",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
        fontFeatureSettings: '"kern" 1, "liga" 1'
      }}
      data-testid="cv-preview"
    >
      {/* HEADER - Photo & Name */}
      <div style={{ 
        textAlign: "center", 
        marginBottom: "6px",
        ...(styles.headerBg && styles.headerBg !== "transparent" ? {
          backgroundColor: styles.headerBg,
          margin: "-20mm -20mm 15px -20mm",
          padding: "20mm 20mm 15px 20mm",
        } : {})
      }}>
        {/* Photo - centered and circular */}
        {cvData.photo && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
            <img 
              src={cvData.photo} 
              alt={cvData.fullName}
              style={{
                width: "85px",
                height: "85px",
                borderRadius: "50%",
                objectFit: "cover",
                border: styles.headerText ? `3px solid ${styles.accent}` : "2px solid #E5E7EB"
              }}
            />
          </div>
        )}
        
        {/* Name */}
        <h1 style={{ 
          fontSize: styles.academic ? "22px" : "24px", 
          fontWeight: "bold", 
          color: styles.headerText || styles.primary,
          margin: "0 0 6px 0",
          fontFamily: fontFamily,
          letterSpacing: styles.minimal ? "2px" : styles.academic ? "1px" : "0",
          textTransform: styles.minimal ? "uppercase" : "none"
        }}>
          {cvData.fullName || "Your Name"}
        </h1>
        
        {/* Contact Line - Phone, Email */}
        <div style={{ 
          fontSize: "11px", 
          color: styles.headerText || "#1F2937", 
          marginBottom: "4px" 
        }}>
          {[cvData.phone, cvData.email].filter(Boolean).join("  |  ")}
        </div>
        
        {/* Full Address */}
        {(cvData.streetAddress || cvData.city || cvData.state || cvData.postalCode || cvData.country) && (
          <div style={{ fontSize: "11px", color: styles.headerText || "#1F2937", marginBottom: "4px" }}>
            {[
              cvData.streetAddress,
              [cvData.city, cvData.state, cvData.postalCode].filter(Boolean).join(", "),
              cvData.country
            ].filter(Boolean).join(", ")}
          </div>
        )}
        
        {/* LinkedIn / Website */}
        {(cvData.linkedin || cvData.website) && (
          <div style={{ fontSize: "11px", color: styles.headerText || "#1F2937", marginBottom: "4px" }}>
            {[cvData.linkedin, cvData.website].filter(Boolean).join("  |  ")}
          </div>
        )}
        
        {/* Date of Birth / Nationality / ID / Languages */}
        {(cvData.dateOfBirth || cvData.nationality || cvData.idNumber || cvData.languagesSpoken) && (
          <div style={{ fontSize: "11px", color: styles.headerText || "#1F2937" }}>
            {cvData.dateOfBirth && `DOB: ${cvData.dateOfBirth}`}
            {cvData.dateOfBirth && (cvData.nationality || cvData.idNumber || cvData.languagesSpoken) && "  |  "}
            {cvData.nationality && `Nationality: ${cvData.nationality}`}
            {cvData.nationality && (cvData.idNumber || cvData.languagesSpoken) && "  |  "}
            {cvData.idNumber && `ID: ${cvData.idNumber}`}
            {cvData.idNumber && cvData.languagesSpoken && "  |  "}
            {cvData.languagesSpoken && `Languages: ${cvData.languagesSpoken}`}
          </div>
        )}
      </div>

      {/* Divider - hidden for templates with header backgrounds */}
      {(!styles.headerBg || styles.headerBg === "transparent") && (
        <div style={{ borderBottom: `2px solid ${styles.primary}`, margin: "15px 0" }}></div>
      )}

      {/* PROFESSIONAL SUMMARY */}
      {cvData.summary && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "8px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            Professional Summary
          </h2>
          <p style={{ fontSize: "11px", color: "#0B1220", lineHeight: "1.6", margin: 0 }}>
            {cvData.summary}
          </p>
        </div>
      )}

      {/* SKILLS */}
      {cvData.skills && cvData.skills.length > 0 && cvData.skills[0].name && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "8px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            {styles.technical ? "Technical Skills" : "Skills"}
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {cvData.skills.map((skill, index) => (
              skill.name && (
                <span key={index} style={{ 
                  fontSize: "10px",
                  backgroundColor: styles.technical ? styles.secondary : styles.accent,
                  color: styles.technical ? styles.primary : styles.primary,
                  padding: "5px 12px",
                  fontWeight: "500",
                  borderRadius: styles.technical ? "4px" : "0",
                  border: styles.technical ? `1px solid ${styles.primary}` : "none"
                }}>
                  {skill.name}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* WORK EXPERIENCE */}
      {cvData.workExperience && cvData.workExperience.length > 0 && cvData.workExperience[0].role && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "10px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            {styles.academic ? "Professional Experience" : "Work Experience"}
          </h2>
          <div>
            {cvData.workExperience.map((work, index) => (
              work.role && (
                <div key={index} style={{ 
                  marginBottom: index < cvData.workExperience.length - 1 ? "12px" : "0",
                  paddingBottom: index < cvData.workExperience.length - 1 ? "12px" : "0",
                  borderBottom: index < cvData.workExperience.length - 1 ? "1px solid #E5E7EB" : "none"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "2px" }}>
                    <h3 style={{ fontSize: "12px", fontWeight: "bold", color: "#0B1220", margin: 0 }}>
                      {work.role}
                    </h3>
                    <span style={{ fontSize: "10px", color: "#0B1220", fontStyle: "italic" }}>
                      {work.startDate} – {work.endDate}
                    </span>
                  </div>
                  <p style={{ fontSize: "11px", color: "#0B1220", fontStyle: "italic", margin: "2px 0 0 0" }}>
                    {work.company}
                  </p>
                  {work.description && formatDescription(work.description)}
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* EDUCATION */}
      {cvData.education && cvData.education.length > 0 && cvData.education[0].degree && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "10px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            Education
          </h2>
          <div>
            {cvData.education.map((edu, index) => (
              edu.degree && (
                <div key={index} style={{ marginBottom: index < cvData.education.length - 1 ? "10px" : "0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <h3 style={{ fontSize: "12px", fontWeight: "bold", color: "#0B1220", margin: 0 }}>
                      {edu.degree}
                    </h3>
                    <span style={{ fontSize: "10px", color: "#0B1220" }}>
                      {edu.graduationYear}
                    </span>
                  </div>
                  <p style={{ fontSize: "11px", color: "#0B1220", margin: "2px 0 0 0" }}>
                    {edu.institution}
                  </p>
                  {edu.subjects && (
                    <p style={{ fontSize: "10px", color: "#0B1220", margin: "2px 0 0 0", fontStyle: "italic" }}>
                      Subjects: {edu.subjects}
                    </p>
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* LANGUAGES */}
      {cvData.languages && cvData.languages.length > 0 && cvData.languages[0].name && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "8px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            Languages
          </h2>
          <div style={{ fontSize: "11px", color: "#0B1220" }}>
            {cvData.languages.map((lang, index) => (
              lang.name && (
                <span key={index}>
                  <strong>{lang.name}</strong> – {lang.proficiency}
                  {index < cvData.languages.length - 1 && ",  "}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* ACHIEVEMENTS */}
      {cvData.achievements && cvData.achievements.length > 0 && cvData.achievements[0] && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "8px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            Achievements
          </h2>
          <ul style={{ margin: 0, padding: "0 0 0 20px", listStyleType: "disc" }}>
            {cvData.achievements.map((achievement, index) => (
              achievement && (
                <li key={index} style={{ fontSize: "11px", color: "#0B1220", marginBottom: "4px" }}>
                  {achievement}
                </li>
              )
            ))}
          </ul>
        </div>
      )}

      {/* PERSONAL ATTRIBUTES */}
      {cvData.attributes && cvData.attributes.length > 0 && cvData.attributes[0] && (
        <div style={{ marginBottom: "15px" }}>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "8px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            Personal Attributes
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {cvData.attributes.map((attr, index) => (
              attr && (
                <span key={index} style={{ 
                  fontSize: "10px",
                  backgroundColor: styles.accent,
                  color: styles.primary,
                  padding: "5px 12px",
                  fontWeight: "500"
                }}>
                  {attr}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* REFERENCES */}
      {cvData.references && cvData.references.length > 0 && cvData.references[0].name && (
        <div>
          <h2 style={{ 
            fontSize: "13px", 
            fontWeight: "bold", 
            color: styles.primary,
            textTransform: "uppercase",
            letterSpacing: styles.minimal ? "3px" : "1px",
            marginBottom: "10px",
            paddingBottom: styles.sectionBorder ? "4px" : "0",
            borderBottom: styles.sectionBorder ? `1px solid ${styles.primary}` : "none"
          }}>
            References
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {cvData.references.map((ref, index) => (
              ref.name && (
                <div key={index}>
                  <h3 style={{ fontSize: "11px", fontWeight: "bold", color: "#0B1220", margin: "0 0 2px 0" }}>
                    {ref.name}
                  </h3>
                  <p style={{ fontSize: "10px", color: "#0B1220", margin: 0 }}>
                    {ref.title}{ref.company && `, ${ref.company}`}
                  </p>
                  {ref.phone && <p style={{ fontSize: "10px", color: "#0B1220", margin: "2px 0 0 0" }}>{ref.phone}</p>}
                  {ref.email && <p style={{ fontSize: "10px", color: "#0B1220", margin: 0 }}>{ref.email}</p>}
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

CVPreview.displayName = "CVPreview";

export default CVPreview;
